import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AuthLayout, AuthField } from "@/components/AuthLayout";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";

const LoginPage = () => {
  const t = useT();
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Where should we send the user after a successful login?
  //  1. `?next=/foo` on the URL wins (used by public links that want to gate a
  //     specific page — e.g. "Log in to comment").
  //  2. `location.state.from` when a <RequireAuth> guard sent them here.
  //  3. Otherwise: admins land straight on /admin (they never use /dashboard
  //     anyway — the "double-hop /dashboard → /admin" was the original bug).
  const params = new URLSearchParams(location.search);
  const nextParam = params.get("next");
  const fromState = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ""}`
    : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setErr(t("auth.err_both")); return; }
    setLoading(true);
    try {
      const user = await login({ email, password });
      const target = nextParam || fromState || (user?.is_admin ? "/admin" : "/dashboard");
      navigate(target, { replace: true });
    } catch (e) {
      setErr(e.message || t("auth.err_generic"));
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout
      testId="page-login"
      title={<>{t("auth.welcome_back")} <span aria-hidden>🍷</span></>}
      subtitle={t("auth.welcome_back_sub")}
      footer={<>{t("auth.no_account")} <Link to="/signup" className="text-[color:var(--gv-pink)]">{t("auth.sign_up")}</Link></>}
    >
      <form onSubmit={onSubmit}>
        <AuthField label={t("auth.email")}>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="gv-input" placeholder="you@email.com" data-testid="login-email" required />
        </AuthField>
        <AuthField label={t("auth.password")} error={err}>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="gv-input" placeholder="••••••••" data-testid="login-password" required />
        </AuthField>
        <button type="submit" disabled={loading} className="btn-primary w-full mb-3" data-testid="login-submit">
          {loading ? `${t("common.loading")}` : <>{t("cta.log_in")} <ArrowRight size={16} /></>}
        </button>
        <div className="text-center">
          <Link to="/forgot-password" className="text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)]">
            {t("auth.forgot_password")}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
